import React, { useState, useEffect, useRef } from "react";
import { FaArrowCircleUp } from "react-icons/fa";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  Legend,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import axios from "axios";
import moment from "moment";
import DatePicker from "react-datepicker";
import { FaRedo } from "react-icons/fa";

const sampleData = [
  {
    month: "Jan",
    year: 2024,
    day: 5,
    users: 1000,
    clientUsers: 400,
    workerUsers: 600,
    activeJobs: 240,
    archivedJobs: 50,
    flaggedJobs: 20,
    applications: 2400,
    revenue: 12000,
    messages: 1500,
    timesheets: 200,
    invoices: 180,
    paymentsProcessed: 12000,
    agreements: 50,
  },
  {
    month: "Feb",
    year: 2024,
    day: 12,
    users: 1100,
    clientUsers: 450,
    workerUsers: 650,
    activeJobs: 260,
    archivedJobs: 60,
    flaggedJobs: 15,
    applications: 2210,
    revenue: 13000,
    messages: 1600,
    timesheets: 220,
    invoices: 190,
    paymentsProcessed: 13000,
    agreements: 60,
  },
  {
    month: "Mar",
    year: 2024,
    day: 22,
    users: 1200,
    clientUsers: 500,
    workerUsers: 700,
    activeJobs: 280,
    archivedJobs: 70,
    flaggedJobs: 10,
    applications: 2290,
    revenue: 14000,
    messages: 1700,
    timesheets: 240,
    invoices: 200,
    paymentsProcessed: 14000,
    agreements: 70,
  },
  {
    month: "Apr",
    year: 2024,
    day: 3,
    users: 1300,
    clientUsers: 550,
    workerUsers: 750,
    activeJobs: 300,
    archivedJobs: 80,
    flaggedJobs: 25,
    applications: 2000,
    revenue: 15000,
    messages: 1800,
    timesheets: 260,
    invoices: 220,
    paymentsProcessed: 15000,
    agreements: 80,
  },
  {
    month: "May",
    year: 2024,
    day: 14,
    users: 1400,
    clientUsers: 600,
    workerUsers: 800,
    activeJobs: 320,
    archivedJobs: 90,
    flaggedJobs: 30,
    applications: 2181,
    revenue: 16000,
    messages: 1900,
    timesheets: 280,
    invoices: 240,
    paymentsProcessed: 16000,
    agreements: 90,
  },
  {
    month: "Jun",
    year: 2024,
    day: 8,
    users: 1500,
    clientUsers: 650,
    workerUsers: 850,
    activeJobs: 340,
    archivedJobs: 100,
    flaggedJobs: 12,
    applications: 2500,
    revenue: 17000,
    messages: 2000,
    timesheets: 300,
    invoices: 260,
    paymentsProcessed: 17000,
    agreements: 100,
  },
  {
    month: "Jul",
    year: 2024,
    day: 19,
    users: 1600,
    clientUsers: 700,
    workerUsers: 900,
    activeJobs: 360,
    archivedJobs: 110,
    flaggedJobs: 18,
    applications: 2100,
    revenue: 18000,
    messages: 2200,
    timesheets: 320,
    invoices: 280,
    paymentsProcessed: 18000,
    agreements: 110,
  },
  {
    month: "Aug",
    year: 2024,
    day: 27,
    users: 1700,
    clientUsers: 750,
    workerUsers: 950,
    activeJobs: 380,
    archivedJobs: 120,
    flaggedJobs: 14,
    applications: 2300,
    revenue: 19000,
    messages: 2300,
    timesheets: 340,
    invoices: 300,
    paymentsProcessed: 19000,
    agreements: 120,
  },
  {
    month: "Sep",
    year: 2024,
    day: 9,
    users: 1800,
    clientUsers: 800,
    workerUsers: 1000,
    activeJobs: 400,
    archivedJobs: 130,
    flaggedJobs: 16,
    applications: 2400,
    revenue: 20000,
    messages: 2400,
    timesheets: 360,
    invoices: 320,
    paymentsProcessed: 20000,
    agreements: 130,
  },
  {
    month: "Oct",
    year: 2024,
    day: 23,
    users: 1900,
    clientUsers: 850,
    workerUsers: 1050,
    activeJobs: 420,
    archivedJobs: 140,
    flaggedJobs: 20,
    applications: 2500,
    revenue: 21000,
    messages: 2500,
    timesheets: 380,
    invoices: 340,
    paymentsProcessed: 21000,
    agreements: 140,
  },
  {
    month: "Nov",
    year: 2024,
    day: 15,
    users: 2000,
    clientUsers: 900,
    workerUsers: 1100,
    activeJobs: 440,
    archivedJobs: 150,
    flaggedJobs: 18,
    applications: 2600,
    revenue: 22000,
    messages: 2600,
    timesheets: 400,
    invoices: 360,
    paymentsProcessed: 22000,
    agreements: 150,
  },
  {
    month: "Dec",
    year: 2024,
    day: 4,
    users: 2100,
    clientUsers: 950,
    workerUsers: 1150,
    activeJobs: 460,
    archivedJobs: 160,
    flaggedJobs: 22,
    applications: 2700,
    revenue: 23000,
    messages: 2700,
    timesheets: 420,
    invoices: 380,
    paymentsProcessed: 23000,
    agreements: 160,
  },
];

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#FF6384",
  "#36A2EB",
];

const AdminDashboard = () => {
  const [showTopBtn, setShowTopBtn] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [userStats, setUserStats] = useState([]);
  const [dateRange, setDateRange] = useState([
    moment().startOf("year").toDate(),
    moment().endOf("year").toDate(),
  ]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showUsers, setShowUsers] = useState(true);
  const [showJobs, setShowJobs] = useState(true);
  const [jobStats, setJobStats] = useState([]);
  const [applicationStats, setApplicationStats] = useState([]);
  const [revenueStats, setRevenueStats] = useState([]);
  const [messageStats, setMessageStats] = useState([]);
  const [timesheetStats, setTimesheetStats] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState([]);
  const [agreementStats, setAgreementStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [revenueRange, setRevenueRange] = useState([0, 20000]);
  const [filteredData, setFilteredData] = useState(sampleData);
  const [summaryData, setSummaryData] = useState({
    users: 0,
    activeJobs: 0,
    archivedJobs: 0,
    flaggedJobs: 0,
    applications: 0,
    revenue: 0,
    messages: 0,
    agreements: 0,
    timesheets: 0,
    invoices: 0,
    paymentsProcessed: 0,
  });

  const sectionIds = [
    "dashboard-users-section",
    "dashboard-jobs-section",
    "dashboard-applications-section",
    "dashboard-messages-section",
    "dashboard-agreements-section",
    "dashboard-timesheets-section",
    "dashboard-invoices-section",
    "dashboard-payments-section",
  ];

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedCategory]);

  useEffect(() => {
    applyFilters();
  }, [
    dateRange,
    selectedCategory,
    searchKeyword,
    showUsers,
    showJobs,
    revenueRange,
  ]);

  useEffect(() => {
    console.log("Show Top Button state:", showTopBtn);
  }, [showTopBtn]);

  const fetchData = async () => {
    setLoading(true);
    try {
      setUserStats(sampleData);
      setJobStats(sampleData);
      setApplicationStats(sampleData);
      setRevenueStats(sampleData);
      setMessageStats(sampleData);
      setTimesheetStats(sampleData);
      setInvoiceStats(sampleData);
      setAgreementStats(sampleData);
    } catch (error) {
      console.error("Failed to fetch data", error);
    } finally {
      setLoading(false);
    }
  };

  const calculateSummaryData = (data) => {
    const initialSummary = {
      users: 0,
      activeJobs: 0,
      archivedJobs: 0,
      flaggedJobs: 0,
      applications: 0,
      revenue: 0,
      messages: 0,
      agreements: 0,
      timesheets: 0,
      invoices: 0,
      paymentsProcessed: 0,
    };

    const summary = data.reduce((acc, item) => {
      Object.keys(acc).forEach((key) => {
        acc[key] += item[key] || 0;
      });
      return acc;
    }, initialSummary);

    console.log("Calculated Summary Data:", summary);
    setSummaryData(summary);
  };

  const initializeData = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    return months.map((month, index) => ({
      month,
      year: 2024,
      users: 0,
      clientUsers: 0,
      workerUsers: 0,
      activeJobs: 0,
      archivedJobs: 0,
      flaggedJobs: 0,
      applications: 0,
      revenue: 0,
      messages: 0,
      timesheets: 0,
      invoices: 0,
      paymentsProcessed: 0,
      agreements: 0,
    }));
  };

  const applyFilters = () => {
    if (!dateRange[0] || !dateRange[1]) {
      console.log("Both start and end dates must be selected.");
      setFilteredData([]);
      calculateSummaryData([]);
      return;
    }

    const startDate = moment(dateRange[0]);
    const endDate = moment(dateRange[1]);

    // Function to initialize data for all months and years found in sampleData
    const initializeData = () => {
      const dataYears = sampleData.map((item) => item.year);
      const uniqueYears = [...new Set(dataYears)]; // Get unique years from sampleData
      const months = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];

      let fullData = [];
      uniqueYears.forEach((year) => {
        months.forEach((month) => {
          fullData.push({
            month,
            year,
            day: 15, // Using mid-month day to avoid end/start of month edge cases
            users: 0,
            clientUsers: 0,
            workerUsers: 0,
            activeJobs: 0,
            archivedJobs: 0,
            flaggedJobs: 0,
            applications: 0,
            revenue: 0,
            messages: 0,
            timesheets: 0,
            invoices: 0,
            paymentsProcessed: 0,
            agreements: 0,
          });
        });
      });
      return fullData;
    };

    let initializedData = initializeData();

    sampleData.forEach((data) => {
      const itemMonthIndex = moment().month(data.month).format("M") - 1; // Convert month name to zero-based index
      const dataIndex = initializedData.findIndex(
        (x) => x.month === data.month && x.year === data.year
      );
      if (dataIndex > -1) {
        initializedData[dataIndex] = { ...initializedData[dataIndex], ...data };
      }
    });

    let filtered = initializedData.filter((item) => {
      const itemMonthIndex = moment().month(item.month).format("M") - 1; // Correct placement of itemMonthIndex
      const itemDate = moment([item.year, itemMonthIndex, item.day]); // Creating moment date without string parsing
      return (
        itemDate.isSameOrBefore(endDate, "day") &&
        itemDate.isSameOrAfter(startDate, "day")
      );
    });

    console.log("Data after Date Filtering:", filtered);

    calculateSummaryData(filtered);
    setFilteredData(filtered);

    if (selectedCategory && selectedCategory !== "All") {
      const sectionMapping = {
        Users: "dashboard-users-section",
        Jobs: "dashboard-jobs-section",
        Applications: "dashboard-applications-section",
        Messages: "dashboard-messages-section",
        Agreements: "dashboard-agreements-section",
        Timesheets: "dashboard-timesheets-section",
        Invoices: "dashboard-invoices-section",
        Payments: "dashboard-payments-section",
      };

      const targetSection = sectionMapping[selectedCategory];
      scrollToSection(targetSection);
    }

    console.log("Final Filtered Data:", filtered);
  };

  const handleCategoryChange = (e) => {
    setSelectedCategory(e.target.value);
  };

  const resetFilters = () => {
    setDateRange([null, null]);

    setTimeout(() => {
      setDateRange([
        moment().startOf("year").toDate(),
        moment().endOf("year").toDate(),
      ]);
    }, 10);

    setSelectedCategory("All");
    setSearchKeyword("");
    setShowUsers(true);
    setShowJobs(true);
    setRevenueRange([0, 20000]);
    applyFilters();
  };

  const scrollToSection = (targetId) => {
    console.log("Attempting to scroll to section:", targetId);
    const targetElement = document.getElementById(targetId);

    sectionIds.forEach((id) => {
      const section = document.getElementById(id);
      const header = section ? section.querySelector("h2") : null;
      if (header) {
        header.classList.remove("highlighted-section-header");
      }
    });

    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "start" });

      const header = targetElement.querySelector("h2");
      if (header) {
        header.classList.add("highlighted-section-header");
        console.log(`Highlighting ${targetId} header`);
      }

      setShowTopBtn(true);
      console.log("Show Top Button state changed to:", showTopBtn);
    } else {
      console.log("Target element not found:", targetId);
    }
  };

  const scrollToTop = () => {
    const summaryElement = document.getElementById("report-dashboard-summary");
    if (summaryElement) {
      summaryElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    setShowTopBtn(false);
    console.log("scrollToTop executed, scrolled to summary section");
  };

  return (
    <div className="admin-report-dashboard">
      <div className="report-dashboard-content">
        <main className="report-dashboard-main">
          <div
            className="rd-invoice-filter-container"
            id="report-dashboard-summary"
          >
            <div className="invoice-filter-itemst">
              <label htmlFor="category-filter">Select a Category</label>
              <select
                id="category-filter"
                value={selectedCategory}
                onChange={handleCategoryChange}
              >
                <option value="All">All</option>
                <option value="Users">Users</option>
                <option value="Jobs">Jobs</option>
                <option value="Applications">Applications</option>
                <option value="Messages">Messages</option>
                <option value="Agreements">Agreements</option>
                <option value="Timesheets">Timesheets</option>
                <option value="Invoices">Invoices</option>
                <option value="Payments">Payments</option>
              </select>
            </div>
            <div className="invoice-filter-itemds">
              <label>Filter by Date Range</label>
              <div className="rd-invoice-date-selects">
                <DatePicker
                  className="rd-date-select"
                  id="start-date-picker"
                  selected={dateRange[0]}
                  onChange={(date) => setDateRange([date, dateRange[1]])}
                  selectsStart
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  placeholderText="Start date"
                />
                <DatePicker
                  id="end-date-picker"
                  selected={dateRange[1]}
                  onChange={(date) => setDateRange([dateRange[0], date])}
                  selectsEnd
                  startDate={dateRange[0]}
                  endDate={dateRange[1]}
                  placeholderText="End date"
                  minDate={dateRange[0]}
                />
              </div>
            </div>
            <div className="rd-clear-filters-container">
              <button className="clear-filters-button" onClick={resetFilters}>
                <FaRedo className="invoice-filter-reset" /> Reset
              </button>
            </div>
            <div className="div-container">
              <button className="rd-button-export">Export</button>
            </div>
          </div>
          <div
            className="summary-cards-container"
            id="report-dashboard-summary"
          >
            <div className="rd-summary-cards">
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-users-section")}
              >
                <h3>Total Users</h3>
                <p>{summaryData.users}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-jobs-section")}
              >
                <h3>Active Jobs</h3>
                <p>{summaryData.activeJobs}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-jobs-section")}
              >
                <h3>Archived Jobs</h3>
                <p>{summaryData.archivedJobs}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-jobs-section")}
              >
                <h3>Flagged Jobs</h3>
                <p>{summaryData.flaggedJobs}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() =>
                  scrollToSection("dashboard-applications-section")
                }
              >
                <h3>Applications</h3>
                <p>{summaryData.applications}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-messages-section")}
              >
                <h3>Messages</h3>
                <p>{summaryData.messages}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-agreements-section")}
              >
                <h3>Agreements</h3>
                <p>{summaryData.agreements}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-timesheets-section")}
              >
                <h3>Timesheets</h3>
                <p>{summaryData.timesheets}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-invoices-section")}
              >
                <h3>Invoices</h3>
                <p>{summaryData.invoices}</p>
              </div>
              <div
                className="rd-summary-card"
                onClick={() => scrollToSection("dashboard-payments-section")}
              >
                <h3>Payments Processed</h3>
                <p>${summaryData.paymentsProcessed}</p>
              </div>
            </div>
          </div>

          <section id="dashboard-users-section">
            <h2>Users Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient id="colorU" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="55%"
                        stopColor="#56b4ee"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#56b4ee"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                    <linearGradient id="colorC" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="55%"
                        stopColor="#0ba98c"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#0ba98c"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                    <linearGradient id="colorW" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="55%"
                        stopColor="#FFBB28"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#FFBB28"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar dataKey="users" fill="url(#colorU)" name="Total Users" />
                  <Bar
                    dataKey="clientUsers"
                    fill="url(#colorC)"
                    name="Client Users"
                  />
                  <Bar
                    dataKey="workerUsers"
                    fill="url(#colorW)"
                    name="Worker Users"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-jobs-section">
            <h2>Jobs Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorActiveJobs"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#82ca9d"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorArchivedJobs"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#6c6e69"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#6c6e69"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                    <linearGradient
                      id="colorFlaggedJobs"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#cf0b0b"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#cf0b0b"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="activeJobs"
                    fill="url(#colorActiveJobs)"
                    name="Active Jobs"
                  />
                  <Bar
                    dataKey="archivedJobs"
                    fill="url(#colorArchivedJobs)"
                    name="Archived Jobs"
                  />
                  <Bar
                    dataKey="flaggedJobs"
                    fill="url(#colorFlaggedJobs)"
                    name="Flagged Jobs"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-applications-section">
            <h2>Job Applications Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorApplications"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#82ca9d"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#82ca9d"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="applications"
                    fill="url(#colorApplications)"
                    name="Job Applications"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-messages-section">
            <h2>Chat Messages Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorMessages"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#FFBB28"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#FFBB28"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="messages"
                    fill="url(#colorMessages)"
                    name="Chat Messages"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-agreements-section">
            <h2>Agreements Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorAgreements"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#4caf50"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#4caf50"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="agreements"
                    fill="url(#colorAgreements)"
                    name="Agreements"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-timesheets-section">
            <h2>Timesheets Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorTimesheets"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#FF8042"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#FF8042"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="timesheets"
                    fill="url(#colorTimesheets)"
                    name="Timesheets"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-invoices-section">
            <h2>Invoices Overview</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={filteredData}>
                  <defs>
                    <linearGradient
                      id="colorInvoices"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="55%"
                        stopColor="#FF6384"
                        stopOpacity={0.8}
                      />
                      <stop
                        offset="95%"
                        stopColor="#FF6384"
                        stopOpacity={0.2}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend verticalAlign="top" height={36} />
                  <Bar
                    dataKey="invoices"
                    fill="url(#colorInvoices)"
                    name="Invoices"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </section>

          <section id="dashboard-payments-section">
            <h2>Financial Reports</h2>
            <div className="chart-wrapper">
              <div className="chart-year">
                Year <span className="user-detail-arrow">➤</span>{" "}
                <strong>2024</strong>
              </div>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={filteredData}>
                    <XAxis dataKey="month" />
                    <YAxis />
                    <CartesianGrid strokeDasharray="3 3" />
                    <Tooltip />
                    <Legend verticalAlign="top" height={36} />
                    <Line
                      type="monotone"
                      dataKey="revenue"
                      stroke="#ff7300"
                      name="Revenue"
                    />
                  </LineChart>
                </ResponsiveContainer>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={filteredData}
                      dataKey="paymentsProcessed"
                      nameKey="month"
                      cx="50%"
                      cy="50%"
                      outerRadius={60}
                      fill="#8884d8"
                    >
                      {filteredData.map((entry, index) => (
                        <Cell
                          key={`cell-${index}`}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>

                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
          </section>
        </main>
      </div>
      {showTopBtn && (
        <FaArrowCircleUp
          className={`scroll-to-top-btn ${showTopBtn ? "show" : ""}`}
          onClick={scrollToTop}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
